// core/src/ai/adapters.ts
function createMemoryStorage(seed = {}) {
  const map = new Map(Object.entries(seed));
  return {
    get: (k) => map.has(k) ? map.get(k) : null,
    set: (k, v) => void map.set(k, v),
    remove: (k) => void map.delete(k)
  };
}
var noopNotifier = {};
function createAbortController() {
  const Native = globalThis.AbortController;
  if (typeof Native === "function") {
    let native = null;
    try {
      native = new Native();
    } catch {
      native = null;
    }
    if (native && native.signal) {
      return {
        get signal() {
          return native.signal;
        },
        abort(reason) {
          native.abort(reason);
        }
      };
    }
  }
  let aborted = false;
  const listeners = /* @__PURE__ */ new Set();
  const signal = {
    get aborted() {
      return aborted;
    },
    addEventListener: (_type, listener) => void listeners.add(listener),
    removeEventListener: (_type, listener) => void listeners.delete(listener)
  };
  return {
    signal,
    abort() {
      if (aborted) return;
      aborted = true;
      for (const l of listeners) {
        try {
          l();
        } catch {
        }
      }
      listeners.clear();
    }
  };
}

// core/src/ai/protocol.ts
function createChatProtocolParser() {
  let buffer = "";
  let finished = false;
  const handleLine = (rawLine, out) => {
    const trimmed = rawLine.trim();
    if (!trimmed) return;
    let jsonStr = trimmed;
    if (jsonStr.startsWith("data:")) {
      jsonStr = jsonStr.slice(jsonStr.indexOf(" ") === -1 ? 5 : jsonStr.indexOf(" ") + 1).trim();
    }
    if (jsonStr === "[DONE]") {
      finished = true;
      out.push({ type: "done" });
      return;
    }
    let parsed;
    try {
      parsed = JSON.parse(jsonStr);
    } catch {
      return;
    }
    if (!parsed || typeof parsed !== "object") return;
    if (parsed.event && parsed.nodeName) {
      out.push({
        type: "progress",
        event: String(parsed.event),
        nodeName: String(parsed.nodeName),
        detail: parsed.detail || "",
        durationMs: parsed.duration_ms
      });
      return;
    }
    if (parsed.type === "clarification") {
      out.push({
        type: "clarification",
        message: parsed.message || "",
        options: Array.isArray(parsed.options) ? parsed.options : []
      });
      return;
    }
    if (parsed.type === "tool_call_start") {
      out.push({
        type: "tool_call_start",
        toolName: String(parsed.tool_name || parsed.toolName || ""),
        arguments: parsed.arguments,
        round: parsed.round
      });
      return;
    }
    if (parsed.type === "tool_call") {
      out.push({
        type: "tool_call",
        toolName: String(parsed.tool_name || parsed.toolName || ""),
        arguments: parsed.arguments,
        result: parsed.result,
        success: parsed.success,
        durationMs: parsed.duration_ms ?? parsed.durationMs ?? null,
        reasoning: parsed.reasoning || "",
        round: parsed.round
      });
      return;
    }
    const choice = parsed.choices && parsed.choices[0];
    const delta = choice && choice.delta;
    const content = delta && delta.content || parsed.content || parsed.message && parsed.message.content || "";
    if (content) out.push({ type: "content", content });
    const thinkingContent = delta && delta.thinking_content || parsed.thinking_content || "";
    if (thinkingContent) out.push({ type: "thinking_content", content: thinkingContent });
    if (parsed.chatId) out.push({ type: "chatId", chatId: String(parsed.chatId) });
    if (parsed.chat_id) out.push({ type: "chatId", chatId: String(parsed.chat_id) });
    if (Array.isArray(parsed.knowledgeTraces) && parsed.knowledgeTraces.length > 0) {
      out.push({ type: "knowledgeTraces", knowledgeTraces: parsed.knowledgeTraces });
    }
    if (Array.isArray(parsed.sources) && parsed.sources.length > 0) {
      out.push({ type: "sources", sources: parsed.sources });
    }
    if (parsed.usage) {
      out.push({ type: "usage", usage: parsed.usage });
    }
    const finishReason = choice && choice.finish_reason ? String(choice.finish_reason) : "";
    if (finishReason && finishReason !== "stop") {
      out.push({ type: "finish", reason: finishReason });
    }
    if (parsed.done || finishReason === "stop") {
      finished = true;
      out.push({ type: "done" });
    }
  };
  return {
    push(chunk) {
      const out = [];
      buffer += chunk;
      const lines = buffer.split("\n");
      buffer = lines.pop() ?? "";
      for (const line of lines) {
        if (finished) break;
        handleLine(line, out);
      }
      return out;
    },
    flush() {
      const out = [];
      if (buffer && !finished) {
        handleLine(buffer, out);
        buffer = "";
      }
      if (!finished) {
        finished = true;
        out.push({ type: "done" });
      }
      return out;
    }
  };
}

// core/src/ai/transport.ts
function buildChatRequestBody(payload) {
  const hasImage = !!payload.imageDataUrl;
  const messageContent = hasImage ? [
    ...payload.question ? [{ type: "text", text: payload.question }] : [],
    { type: "image_url", image_url: { url: payload.imageDataUrl } }
  ] : payload.question || "";
  const body = {
    model: payload.model || payload.agentCode || "default",
    messages: [{ role: "user", content: messageContent }],
    stream: payload.stream === void 0 ? true : payload.stream,
    temperature: payload.temperature,
    max_tokens: payload.maxTokens,
    top_p: payload.topP,
    frequency_penalty: payload.frequencyPenalty,
    presence_penalty: payload.presencePenalty,
    tools: payload.tools,
    tool_choice: payload.toolChoice,
    chat_id: payload.chatId,
    agent_id: payload.agentId,
    knowledge_ids: payload.knowledgeIds,
    rag_type: payload.ragType,
    // 结构化澄清选择：后端直接按 placeId 等权威字段继续，不再靠名称重查
    clarify_choice: payload.clarifyChoice,
    // 续写（中断后「继续生成」）：前缀是已显示正文，原问题供后端检索使用
    resume_prefix: payload.resumePrefix,
    resume_question: payload.resumeQuestion,
    // 恒发 boolean：`|| undefined` 会把 false 吃掉，导致后端关不掉深度思考
    thinking: payload.thinking
  };
  for (const key of Object.keys(body)) {
    if (body[key] === void 0) delete body[key];
  }
  return body;
}
function toNativeAbortSignal(signal) {
  const g = globalThis;
  if (!signal) return void 0;
  if (typeof g.AbortSignal === "function" && signal instanceof g.AbortSignal) return signal;
  const Native = g.AbortController;
  if (typeof Native !== "function") return signal;
  let ctrl;
  try {
    ctrl = new Native();
  } catch {
    return signal;
  }
  if (!ctrl || !ctrl.signal) return signal;
  if (signal.aborted) {
    ctrl.abort();
    return ctrl.signal;
  }
  if (typeof signal.addEventListener === "function") {
    ;
    signal.addEventListener(
      "abort",
      () => {
        try {
          ctrl.abort();
        } catch {
        }
      },
      { once: true }
    );
  }
  return ctrl.signal;
}
function createFetchTransport(options) {
  const endpoint = options.endpoint || "/v1/chat/completions";
  const url = options.baseUrl.replace(/\/$/, "") + endpoint;
  return {
    async *streamChat(payload, signal) {
      const fetchImpl = options.fetchImpl || globalThis.fetch;
      if (typeof fetchImpl !== "function") {
        yield { type: "error", content: "\u5F53\u524D\u73AF\u5883\u4E0D\u652F\u6301 fetch\uFF0C\u8BF7\u6CE8\u5165\u81EA\u5B9A\u4E49 transport" };
        yield { type: "done" };
        return;
      }
      const headers = {
        "Content-Type": "application/json",
        ...options.headers || {}
      };
      const token = options.getToken ? options.getToken() : null;
      if (token) headers.Authorization = "Bearer " + token;
      let res;
      try {
        res = await fetchImpl(url, {
          method: "POST",
          headers,
          body: JSON.stringify(buildChatRequestBody(payload)),
          // 不能直接把内核的 AbortSignalLike 交给浏览器 fetch（brand check 会拒绝）
          signal: toNativeAbortSignal(signal)
        });
      } catch (err) {
        if (err && (err.name === "AbortError" || err.code === 20)) {
          yield { type: "done" };
          return;
        }
        yield { type: "error", content: err && err.message || "\u7F51\u7EDC\u8BF7\u6C42\u5931\u8D25" };
        yield { type: "done" };
        return;
      }
      if (!res.ok) {
        let message = "\u8BF7\u6C42\u5931\u8D25\uFF1AHTTP " + res.status;
        let errorType = "server_error";
        let errorCode = "internal_error";
        try {
          const text = await res.text();
          if (text) {
            try {
              const parsed = JSON.parse(text);
              if (parsed && parsed.error) {
                message = parsed.error.message || message;
                errorType = parsed.error.type || errorType;
                errorCode = parsed.error.code || errorCode;
              } else {
                message = text;
              }
            } catch {
              message = text;
            }
          }
        } catch {
        }
        yield { type: "error", content: message, errorType, errorCode, statusCode: res.status };
        yield { type: "done" };
        return;
      }
      const parser = createChatProtocolParser();
      if (!res.body || typeof res.body.getReader !== "function") {
        const text = await res.text();
        for (const ev of parser.push(text)) yield ev;
        for (const ev of parser.flush()) yield ev;
        return;
      }
      const TD = globalThis.TextDecoder;
      const decoder = TD ? new TD("utf-8") : null;
      const reader = res.body.getReader();
      try {
        for (; ; ) {
          const { done, value } = await reader.read();
          if (done) break;
          const text = decoder ? decoder.decode(value, { stream: true }) : String(value);
          for (const ev of parser.push(text)) {
            yield ev;
            if (ev.type === "done") return;
          }
        }
      } finally {
        const flushed = parser.flush();
        for (const ev of flushed) {
          if (ev.type === "done") return;
          yield ev;
        }
      }
    }
  };
}

// core/src/ai/render.ts
function currentTime() {
  const d = /* @__PURE__ */ new Date();
  const pad = (n) => String(n).padStart(2, "0");
  return `${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
}
function truncate(text, len) {
  if (!text) return "";
  return text.length > len ? text.slice(0, len) + "..." : text;
}
function formatToolArguments(args) {
  if (!args) return "";
  if (typeof args === "object") {
    try {
      return JSON.stringify(args, null, 2);
    } catch {
      return String(args);
    }
  }
  const raw = String(args);
  try {
    return JSON.stringify(JSON.parse(raw), null, 2);
  } catch {
    return raw;
  }
}
function formatToolResult(result) {
  if (!result) return "";
  if (typeof result === "object") {
    try {
      return JSON.stringify(result, null, 2);
    } catch {
      return String(result);
    }
  }
  const raw = String(result);
  try {
    return JSON.stringify(JSON.parse(raw), null, 2);
  } catch {
    return raw;
  }
}
function escapeHtml(str) {
  if (!str) return "";
  return str.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;");
}
function isWebSearchTool(toolName) {
  if (!toolName) return false;
  const name = toolName.toLowerCase();
  return name === "websearch" || name === "\u4E92\u8054\u7F51\u641C\u7D22" || name === "internet_search";
}
function isReportTool(toolName) {
  if (!toolName) return false;
  const name = toolName.toLowerCase();
  return name === "report" || name === "\u62A5\u544A\u751F\u6210" || name === "\u62A5\u544A";
}
function getReportCards(msg) {
  if (!msg || !msg.toolCalls) return [];
  const cards = [];
  for (const tc of msg.toolCalls) {
    if (!isReportTool(tc.toolName) || !tc.result) continue;
    try {
      const parsed = JSON.parse(String(tc.result));
      if (parsed && parsed.type === "report") cards.push(parsed);
    } catch {
    }
  }
  return cards;
}
function getWebSearchQuery(msg) {
  if (!msg) return "";
  if (msg.optimizedSearchQuery) return String(msg.optimizedSearchQuery);
  if (!msg.toolCalls) return "";
  const tc = msg.toolCalls.find((t) => isWebSearchTool(t.toolName));
  if (!tc || !tc.arguments) return "";
  try {
    const args = typeof tc.arguments === "string" ? JSON.parse(tc.arguments) : tc.arguments;
    return args.query || "";
  } catch {
    return String(tc.arguments);
  }
}
function hasNonWebSearchTools(msg) {
  if (!msg || !msg.toolCalls || msg.toolCalls.length === 0) return false;
  return msg.toolCalls.some((tc) => !isWebSearchTool(tc.toolName) && !isReportTool(tc.toolName));
}
function isWebSearchExpanded(msg) {
  return !!(msg && msg._webSearchExpanded);
}
function parseWebSearchResults(toolCalls) {
  if (!toolCalls || toolCalls.length === 0) return { results: [], optimizedQuery: "" };
  const results = [];
  let optimizedQuery = "";
  for (const tc of toolCalls) {
    if (!isWebSearchTool(tc.toolName) || !tc.result) continue;
    try {
      const parsed = JSON.parse(String(tc.result));
      if (parsed.optimized_query) optimizedQuery = parsed.optimized_query;
      if (parsed.success && Array.isArray(parsed.results)) {
        for (const item of parsed.results) {
          if (item.title || item.url) {
            results.push({ title: item.title || "", url: item.url || "", snippet: item.snippet || "" });
          }
        }
      }
    } catch {
    }
  }
  return { results, optimizedQuery };
}
var TOOL_NAME_MAP = {
  device_data_query: "\u67E5\u8BE2\u8BBE\u5907\u6570\u636E",
  device_query: "\u67E5\u8BE2\u8BBE\u5907",
  knowledge_search: "\u68C0\u7D22\u77E5\u8BC6\u5E93",
  websearch: "\u641C\u7D22\u4E92\u8054\u7F51",
  device_status: "\u67E5\u8BE2\u8BBE\u5907\u72B6\u6001",
  device_command_list: "\u67E5\u8BE2\u8BBE\u5907\u6307\u4EE4",
  device_command_send: "\u4E0B\u53D1\u8BBE\u5907\u6307\u4EE4",
  place_query: "\u67E5\u8BE2\u573A\u5730\uFF08\u5858\u53E3/\u5E93\u623F\uFF09",
  feed_record_query: "\u67E5\u8BE2\u6295\u5582\u8BB0\u5F55",
  skill_script_exec: "\u6267\u884C\u6280\u80FD\u811A\u672C",
  user_create: "\u521B\u5EFA\u7528\u6237",
  scene_create: "\u521B\u5EFA\u573A\u666F\u8054\u52A8",
  device_create: "\u521B\u5EFA\u8BBE\u5907",
  create_project: "\u521B\u5EFA\u9879\u76EE"
};
function getToolDisplayName(toolName) {
  if (!toolName) return "\u5DE5\u5177";
  if (toolName.startsWith("agent_invoke_")) {
    return "\u8C03\u7528\u667A\u80FD\u4F53\xB7" + toolName.slice("agent_invoke_".length);
  }
  return TOOL_NAME_MAP[toolName] || toolName;
}
var STAGE_NAME_MAP = {
  __pending_submit: "\u6B63\u5728\u63D0\u4EA4\u7ED9 AI \u670D\u52A1",
  InputValidation: "\u7406\u89E3\u95EE\u9898",
  AgentResolution: "\u8DEF\u7531\u667A\u80FD\u4F53",
  KnowledgeResolution: "\u68C0\u7D22\u77E5\u8BC6",
  ToolResolutionStage: "\u52A0\u8F7D\u5DE5\u5177",
  InputGuardrailStage: "\u8F93\u5165\u5B89\u5168\u68C0\u67E5",
  BalanceCheckStage: "\u989D\u5EA6\u68C0\u67E5",
  MemoryRetrievalStage: "\u53EC\u56DE\u8BB0\u5FC6",
  PromptAssemblyStage: "\u7EC4\u88C5\u4E0A\u4E0B\u6587",
  LlmExecutionStage: "\u6A21\u578B\u63A8\u7406",
  ConstrainedGenerationStage: "\u7EA6\u675F\u751F\u6210",
  OutputGuardrailStage: "\u8F93\u51FA\u5B89\u5168\u68C0\u67E5",
  BillingStage: "\u7528\u91CF\u8BA1\u8D39",
  MemoryFlushTriggerStage: "\u8BB0\u5FC6\u6574\u7406",
  PersistenceStage: "\u4FDD\u5B58\u4F1A\u8BDD"
};
function getStageDisplayName(name) {
  if (!name) return "\u5904\u7406\u4E2D";
  return STAGE_NAME_MAP[name] || name;
}
var TURN_ACTIONS = [
  {
    key: "understand",
    title: "\u7406\u89E3\u95EE\u9898",
    desc: "\u6821\u9A8C\u8F93\u5165\u3001\u8BC6\u522B\u610F\u56FE\u3001\u8DEF\u7531\u5230\u5BF9\u5E94\u667A\u80FD\u4F53",
    stages: ["__pending_submit", "InputValidation", "InputGuardrailStage", "AgentResolution", "BalanceCheckStage"]
  },
  {
    key: "retrieve",
    title: "\u68C0\u7D22\u77E5\u8BC6",
    desc: "\u53EC\u56DE\u957F\u671F\u8BB0\u5FC6\u4E0E\u77E5\u8BC6\u5E93\u5185\u5BB9\uFF0C\u5E76\u7EC4\u88C5\u4E0A\u4E0B\u6587",
    stages: ["KnowledgeResolution", "MemoryRetrievalStage", "PromptAssemblyStage"]
  },
  {
    key: "tools",
    title: "\u8C03\u7528\u5DE5\u5177",
    desc: "\u6309\u9700\u8C03\u7528\u4E1A\u52A1\u5DE5\u5177\u83B7\u53D6\u771F\u5B9E\u6570\u636E",
    stages: ["ToolResolutionStage"]
  },
  {
    key: "generate",
    title: "\u751F\u6210\u56DE\u7B54",
    desc: "\u6A21\u578B\u63A8\u7406\u3001\u7EA6\u675F\u751F\u6210\u4E0E\u8F93\u51FA\u5B89\u5168\u68C0\u67E5",
    stages: ["LlmExecutionStage", "ConstrainedGenerationStage", "OutputGuardrailStage"]
  },
  {
    key: "finish",
    title: "\u6536\u5C3E",
    desc: "\u7528\u91CF\u8BA1\u8D39\u3001\u8BB0\u5FC6\u6574\u7406\u4E0E\u4F1A\u8BDD\u843D\u5E93",
    stages: ["BillingStage", "MemoryFlushTriggerStage", "PersistenceStage"]
  }
];
var STAGE_GROUP_MAP = (() => {
  const map = {};
  for (const action of TURN_ACTIONS) {
    for (const name of action.stages) map[name] = action.key;
  }
  return map;
})();
function worstStatus(list) {
  if (list.some((s) => s === "running")) return "running";
  if (list.some((s) => s === "error")) return "error";
  if (list.length > 0 && list.every((s) => s === "done")) return "done";
  return "pending";
}
function buildTurnActions(msg) {
  const stages = msg && msg.stages || [];
  const chain = msg && msg.toolChain || [];
  const buckets = {
    understand: [],
    retrieve: [],
    tools: [],
    generate: [],
    finish: []
  };
  for (const st of stages) {
    const key = STAGE_GROUP_MAP[st.name] || "generate";
    buckets[key].push({
      key: "s:" + st.name,
      label: getStageDisplayName(st.name),
      status: st.status,
      durationMs: st.durationMs || 0,
      detail: st.detail || "",
      kind: "stage"
    });
  }
  for (let i = 0; i < chain.length; i++) {
    const tc = chain[i];
    const running = !!tc.pending;
    buckets.tools.push({
      key: "t:" + i + ":" + (tc.toolName || ""),
      label: getToolDisplayName(tc.toolName),
      status: running ? "running" : tc.success === false ? "error" : "done",
      durationMs: tc.durationMs || 0,
      detail: summarizeToolOutcome(tc.result),
      kind: "tool",
      tool: tc
    });
  }
  return TURN_ACTIONS.map((def) => {
    const steps = buckets[def.key];
    const status = worstStatus(steps.map((s) => s.status));
    const durationMs = steps.reduce((a, s) => a + (s.durationMs || 0), 0);
    let summary = "";
    if (def.key === "tools") {
      const total = steps.length;
      if (total === 0) {
        summary = "";
      } else if (status === "running") {
        const cur = steps.find((s) => s.status === "running");
        summary = cur ? cur.label + "\u2026" : "\u51C6\u5907\u8C03\u7528";
      } else {
        const failed = steps.filter((s) => s.status === "error").length;
        summary = total + " \u6B21\u8C03\u7528" + (failed > 0 ? ` \xB7 ${failed} \u6B21\u5931\u8D25` : "");
      }
    } else if (status === "running") {
      const cur = steps.find((s) => s.status === "running");
      summary = cur ? cur.detail || cur.label : "\u8FDB\u884C\u4E2D";
    } else if (status === "error") {
      const bad = steps.find((s) => s.status === "error");
      summary = bad ? (bad.detail || bad.label) + " \u5931\u8D25" : "\u5931\u8D25";
    } else if (status === "done") {
      summary = steps.length > 1 ? `${steps.length} \u4E2A\u6B65\u9AA4` : steps[0]?.label || "";
    }
    return {
      key: def.key,
      title: def.title,
      desc: def.desc,
      status,
      durationMs,
      summary,
      steps,
      // 工具动作没有工具也没有工具阶段时不占位（普通问答不该出现空行）
      visible: def.key === "tools" ? steps.length > 0 : steps.length > 0
    };
  });
}
function turnCardSummary(msg) {
  const actions = buildTurnActions(msg).filter((a) => a.visible);
  if (!actions.length) return "";
  const running = actions.find((a) => a.status === "running");
  if (running) {
    const done = actions.filter((a) => a.status === "done").length;
    return "\u5904\u7406\u4E2D \xB7 " + running.title + ` \xB7 ${done}/${actions.length}`;
  }
  const total = actions.reduce((a, x) => a + (x.durationMs || 0), 0);
  const failed = actions.some((a) => a.status === "error");
  return (failed ? "\u5DF2\u5B8C\u6210\uFF08\u6709\u5931\u8D25\uFF09" : "\u5DF2\u5B8C\u6210") + ` \xB7 ${actions.length} \u4E2A\u52A8\u4F5C` + (total > 0 ? ` \xB7 \u5171 ${(total / 1e3).toFixed(1)}s` : "");
}
function isTurnCardExpanded(msg) {
  if (!msg) return false;
  if (msg._turnCardExpanded !== void 0) return msg._turnCardExpanded === true;
  return hasRunningStage(msg) || (msg.toolChain || []).some((t) => t.pending);
}
function hasRunningStage(msg) {
  return !!(msg && msg.stages && msg.stages.some((s) => s.status === "running"));
}
function stagePanelSummary(msg) {
  if (!msg || !msg.stages || !msg.stages.length) return "";
  const stages = msg.stages;
  const total = stages.length;
  const cur = stages.find((s) => s.status === "running");
  if (cur) {
    const done = stages.filter((s) => s.status !== "running").length;
    return "\u5904\u7406\u4E2D \xB7 " + getStageDisplayName(cur.name) + ` \xB7 ${done}/${total} \u6B65`;
  }
  const sum = stages.reduce((a, s) => a + (s.durationMs || 0), 0);
  return `\u5DF2\u5B8C\u6210 ${total} \u4E2A\u6B65\u9AA4` + (sum > 0 ? ` \xB7 \u5171 ${(sum / 1e3).toFixed(1)}s` : "");
}
function isStagePanelExpanded(msg) {
  if (!msg) return false;
  return msg._stagePanelExpanded === true;
}
function summarizeToolOutcome(resultStr) {
  if (!resultStr) return "";
  let r = null;
  try {
    r = typeof resultStr === "string" ? JSON.parse(resultStr) : resultStr;
  } catch {
    r = null;
  }
  if (!r || typeof r !== "object") return String(resultStr).slice(0, 48);
  if (r.summary) return String(r.summary).slice(0, 60);
  if (r.clarify === true) return "\u9700\u786E\u8BA4\uFF1A" + String(r.hint || r.message || "").slice(0, 36);
  if (r.success === false || typeof r.code === "number" && r.code >= 400 || r.error) {
    return "\u5931\u8D25\uFF1A" + String(r.msg || r.message || r.error || "").slice(0, 40);
  }
  const n = Array.isArray(r.data) ? r.data.length : typeof r.count === "number" ? r.count : null;
  if (n !== null) return n > 0 ? `\u8FD4\u56DE ${n} \u6761\u7ED3\u679C` : "\u65E0\u5339\u914D\u7ED3\u679C";
  if (r.success === true) return "\u6267\u884C\u6210\u529F";
  const keys = Object.keys(r);
  return keys.length ? "\u5DF2\u8FD4\u56DE " + keys.slice(0, 3).join("/") : "";
}
function toolFlowSummary(msg) {
  const chain = msg && msg.toolChain || [];
  if (!chain.length) return "";
  const running = chain.some((t) => t.pending);
  const total = chain.reduce((a, t) => a + (t.durationMs || 0), 0);
  if (running) {
    const done = chain.filter((t) => !t.pending).length;
    return `${done}/${chain.length} \u6B65 \xB7 \u8FDB\u884C\u4E2D`;
  }
  return `${chain.length} \u6B65` + (total > 0 ? ` \xB7 ${(total / 1e3).toFixed(1)}s` : "");
}
function parseReasoningRounds(text) {
  if (!text) return [];
  const re = /【?思考轮\s*\d+\s*[｜|：:]\s*([^】\n]+)】?/g;
  const matches = [...text.matchAll(re)];
  if (!matches.length) return [];
  const rounds = [];
  for (let i = 0; i < matches.length; i++) {
    const m = matches[i];
    const numMatch = m[0].match(/思考轮\s*(\d+)/);
    const contentStart = m.index + m[0].length;
    const contentEnd = i + 1 < matches.length ? matches[i + 1].index : text.length;
    rounds.push({
      num: numMatch ? numMatch[1] : String(i + 1),
      title: String(m[1]).trim(),
      content: text.slice(contentStart, contentEnd).replace(/^\n+|\n+$/g, "")
    });
  }
  return rounds;
}
function getGroupTitle(group) {
  if (!group) return "";
  const rounds = parseReasoningRounds(group.reasoning);
  if (rounds.length > 0 && rounds[0].title) return rounds[0].title;
  if (group.tools && group.tools.length > 0) return getToolDisplayName(group.tools[0].toolName);
  return "";
}
function groupToolChainByRound(msg) {
  if (!msg || !msg.toolChain || msg.toolChain.length === 0) return [];
  const groups = [];
  const map = {};
  for (const tc of msg.toolChain) {
    const r = tc.round !== void 0 && tc.round !== null ? tc.round : 0;
    const key = String(r);
    if (!map[key]) {
      map[key] = { round: r, tools: [], reasoning: "", _expanded: false };
      groups.push(map[key]);
    }
    map[key].tools.push(tc);
  }
  for (const g of groups) {
    g.reasoning = g.tools[0] && g.tools[0].reasoning || "";
  }
  return groups;
}
function isToolChainGroupExpanded(msg, round) {
  if (!msg) return false;
  const state = msg._groupExpanded;
  if (state && state[String(round)] !== void 0) return state[String(round)] === true;
  const chain = msg.toolChain || [];
  return chain.some((t) => t.round === round && t.pending);
}
function openReportUrl(url) {
  if (!url) return;
  const g = typeof globalThis !== "undefined" ? globalThis : null;
  if (g && typeof g.open === "function") {
    g.open(url, "_blank");
    return;
  }
  if (g && g.window && typeof g.window.open === "function") {
    g.window.open(url, "_blank");
    return;
  }
}
function overallConfidenceValue(msg) {
  const traces = msg?.knowledgeTraces;
  if (!traces || traces.length === 0) return 0;
  return Math.max(...traces.map((t) => Number(t.confidence) || 0));
}
function getHitImages(msg) {
  const urls = [];
  const seen = /* @__PURE__ */ new Set();
  const traces = msg && msg.knowledgeTraces || [];
  for (const trace of traces) {
    const imgs = trace.images || [];
    for (const url of imgs) {
      if (url && !seen.has(url)) {
        seen.add(url);
        urls.push(url);
      }
    }
  }
  return urls;
}
function normalizeImageUrl(url) {
  if (!url) return "";
  return url;
}
var feedImageCache = /* @__PURE__ */ new WeakMap();
function getFeedRecordImages(msg) {
  const calls = msg && Array.isArray(msg.toolCalls) ? msg.toolCalls : [];
  const out = [];
  const seen = /* @__PURE__ */ new Set();
  for (const tc of calls) {
    if (!tc || tc.toolName !== "feed_record_query" || !tc.result) continue;
    let urls;
    const cached = feedImageCache.get(tc);
    if (cached && cached.raw === tc.result) {
      urls = cached.out;
    } else {
      urls = parseFeedRecordImageUrls(tc.result);
      feedImageCache.set(tc, { raw: tc.result, out: urls });
    }
    for (const url of urls) {
      if (!seen.has(url)) {
        seen.add(url);
        out.push(url);
      }
    }
  }
  return out;
}
function parseFeedRecordImageUrls(raw) {
  let result;
  try {
    result = JSON.parse(raw);
  } catch {
    return [];
  }
  const rows = result && Array.isArray(result.rows) ? result.rows : [];
  const out = [];
  for (const r of rows) {
    const url = normalizeImageUrl(r && r.imgUrl);
    if (url) out.push(url);
  }
  return out;
}
function overallConfidenceLevel(msg) {
  const conf = overallConfidenceValue(msg);
  if (conf >= 0.75) return "high";
  if (conf >= 0.5) return "medium";
  return "low";
}
function confidenceTagType(level) {
  if (level === "high") return "success";
  if (level === "medium") return "warning";
  return "danger";
}
function confidenceLabel(level) {
  if (level === "high") return "\u9AD8\u7F6E\u4FE1\u5EA6";
  if (level === "medium") return "\u4E2D\u7F6E\u4FE1\u5EA6";
  return "\u4F4E\u7F6E\u4FE1\u5EA6";
}
function matchSourceLabel(source) {
  if (source === "vector") return "\u5411\u91CF\u5339\u914D";
  if (source === "bm25") return "\u5173\u952E\u8BCD\u5339\u914D";
  if (source === "graph") return "\u56FE\u8C31\u5339\u914D";
  return "\u6DF7\u5408\u5339\u914D";
}

// core/src/ai/renderKey.ts
var renderKeySeq = 0;
function nextRenderKey() {
  renderKeySeq += 1;
  return renderKeySeq;
}
function bumpRenderKey(msg) {
  const token = nextRenderKey();
  if (msg) msg._renderKey = token;
  return token;
}

// core/src/ai/session.ts
var FLUSH_INTERVAL = 50;
var FLUSH_BUFFER_CHARS = 300;
var CHAT_IMG_STORAGE_PREFIX = "chatImages_";
var CHAT_IMG_CACHE_MAX = 12;
var RESUME_SEAM_MAX_OVERLAP = 120;
var RESUME_SEAM_MIN_OVERLAP = 8;
function createChatSession(options) {
  const notifier = options.notifier || noopNotifier;
  const storage = options.storage;
  const flushInterval = options.flushInterval ?? FLUSH_INTERVAL;
  const flushThreshold = options.flushThreshold ?? FLUSH_BUFFER_CHARS;
  let messages = [];
  let activeChatId = null;
  let loading = false;
  let msgIdCounter = 0;
  let onSaveSession = options.onSaveSession || null;
  let onAfterSend = options.onAfterSend || null;
  let controller = null;
  let contentBuffer = "";
  let thinkingBuffer = "";
  let contentTimer = null;
  let thinkingTimer = null;
  let streamTarget = null;
  const listeners = /* @__PURE__ */ new Set();
  function emit() {
    const snapshot = messages.slice();
    for (const listener of listeners) {
      try {
        listener(snapshot);
      } catch {
      }
    }
  }
  function nextMsgId() {
    msgIdCounter += 1;
    return "msg_" + Date.now() + "_" + msgIdCounter;
  }
  function currentStreamTarget() {
    if (streamTarget) return streamTarget;
    const last = messages[messages.length - 1];
    return last && last.role === "assistant" ? last : null;
  }
  function flushContent() {
    if (contentTimer) {
      clearTimeout(contentTimer);
      contentTimer = null;
    }
    if (!contentBuffer) return;
    const buffered = contentBuffer;
    contentBuffer = "";
    const target = currentStreamTarget();
    if (target) {
      target.content += buffered;
      target._renderKey = nextRenderKey();
      emit();
    }
  }
  function flushThinking() {
    if (thinkingTimer) {
      clearTimeout(thinkingTimer);
      thinkingTimer = null;
    }
    if (!thinkingBuffer) return;
    const buffered = thinkingBuffer;
    thinkingBuffer = "";
    const target = currentStreamTarget();
    if (target) {
      target.reasoning = (target.reasoning || "") + buffered;
      target._renderKey = nextRenderKey();
      emit();
    }
  }
  function appendContent(chunk) {
    contentBuffer += chunk;
    if (contentBuffer.length >= flushThreshold) {
      flushContent();
      return;
    }
    if (!contentTimer) {
      contentTimer = setTimeout(() => {
        contentTimer = null;
        flushContent();
      }, flushInterval);
    }
  }
  function appendThinking(chunk) {
    thinkingBuffer += chunk;
    if (thinkingBuffer.length >= flushThreshold) {
      flushThinking();
      return;
    }
    if (!thinkingTimer) {
      thinkingTimer = setTimeout(() => {
        thinkingTimer = null;
        flushThinking();
      }, flushInterval);
    }
  }
  function applyEvent(msg, event) {
    switch (event.type) {
      case "content":
        appendContent(event.content);
        break;
      case "thinking_content":
        appendThinking(event.content);
        break;
      case "progress": {
        msg.progress = { event: event.event, nodeName: event.nodeName, detail: event.detail };
        const stages = msg.stages || (msg.stages = []);
        if (event.event === "node_started") {
          for (const s of stages) if (s.status === "running") s.status = "done";
          stages.push({ name: event.nodeName, status: "running", detail: "", durationMs: null, startAt: Date.now() });
        } else if (event.event === "node_finished") {
          const st = stages.find((s) => s.name === event.nodeName && s.status === "running");
          if (st) {
            st.status = "done";
            st.durationMs = event.durationMs ?? null;
          }
        } else if (event.event === "node_progress") {
          const named = event.nodeName ? stages.find((s) => s.name === event.nodeName && s.status === "running") : null;
          if (named) {
            named.detail = event.detail || "";
          } else {
            for (let i = stages.length - 1; i >= 0; i--) {
              if (stages[i].status === "running") {
                stages[i].detail = event.detail || "";
                break;
              }
            }
          }
        } else if (event.event === "node_message") {
          const text = (event.detail || "").trim();
          if (text) {
            const notices = msg.notices || (msg.notices = []);
            if (!notices.includes(text)) notices.push(text);
          }
        } else if (event.event === "node_error") {
          const st = stages.find((s) => s.name === event.nodeName && s.status === "running");
          if (st) st.status = "error";
        }
        msg._renderKey = nextRenderKey();
        emit();
        break;
      }
      case "chatId": {
        if (event.chatId && !activeChatId) {
          activeChatId = event.chatId;
          if (storage) {
            try {
              const pending = storage.get(CHAT_IMG_STORAGE_PREFIX + "pending");
              if (pending) {
                const list = JSON.parse(pending);
                if (Array.isArray(list) && list.length > 0) {
                  const realKey = CHAT_IMG_STORAGE_PREFIX + event.chatId;
                  const realList = JSON.parse(storage.get(realKey) || "[]");
                  storage.set(realKey, JSON.stringify(realList.concat(list)));
                }
                storage.remove(CHAT_IMG_STORAGE_PREFIX + "pending");
              }
            } catch {
            }
          }
        }
        break;
      }
      case "tool_call_start": {
        const chain = msg.toolChain || (msg.toolChain = []);
        chain.push({
          toolName: event.toolName,
          arguments: event.arguments,
          result: "",
          success: null,
          durationMs: null,
          pending: true,
          startAt: Date.now(),
          reasoning: "",
          round: event.round,
          _idx: chain.length,
          _expanded: false
        });
        msg._renderKey = nextRenderKey();
        emit();
        break;
      }
      case "tool_call": {
        const record = {
          toolName: event.toolName,
          arguments: event.arguments,
          result: event.result,
          success: event.success,
          durationMs: event.durationMs ?? null,
          reasoning: event.reasoning || "",
          round: event.round
        };
        const calls = msg.toolCalls || (msg.toolCalls = []);
        calls.push(record);
        const chain = msg.toolChain || (msg.toolChain = []);
        let pending = null;
        const sameArgs = (a, b) => {
          if (a == null || b == null) return false;
          const norm = (v) => {
            try {
              return typeof v === "string" ? v : JSON.stringify(v);
            } catch {
              return String(v);
            }
          };
          return norm(a) === norm(b);
        };
        for (let i = 0; i < chain.length; i++) {
          const node = chain[i];
          if (node.pending && node.toolName === event.toolName && node.round === event.round && sameArgs(node.arguments, event.arguments)) {
            pending = node;
            break;
          }
        }
        if (!pending) {
          for (let i = 0; i < chain.length; i++) {
            const node = chain[i];
            if (node.pending && node.toolName === event.toolName && node.round === event.round) {
              pending = node;
              break;
            }
          }
        }
        if (pending) {
          pending.pending = false;
          pending.result = event.result;
          pending.success = event.success;
          pending.durationMs = event.durationMs ?? null;
          pending.reasoning = event.reasoning || "";
        } else {
          chain.push({ ...record, pending: false, _idx: chain.length, _expanded: false });
        }
        if (isWebSearchTool(event.toolName)) {
          const { results, optimizedQuery } = parseWebSearchResults([
            { toolName: event.toolName, result: event.result }
          ]);
          if (results.length > 0) {
            msg.webSearchResults = [...msg.webSearchResults || [], ...results];
            msg.optimizedSearchQuery = optimizedQuery || msg.optimizedSearchQuery || "";
          }
        }
        msg._renderKey = nextRenderKey();
        emit();
        break;
      }
      case "sources":
        msg.sources = event.sources;
        msg._renderKey = nextRenderKey();
        emit();
        break;
      case "knowledgeTraces":
        msg.knowledgeTraces = event.knowledgeTraces;
        msg._renderKey = nextRenderKey();
        emit();
        break;
      case "clarification": {
        flushContent();
        const options2 = Array.isArray(event.options) ? event.options : [];
        if (options2.length === 0) {
          const text = (event.message || "").trim();
          if (text) {
            const notices = msg.notices || (msg.notices = []);
            if (!notices.includes(text)) notices.push(text);
          }
          msg._renderKey = nextRenderKey();
          emit();
          break;
        }
        msg.content = "";
        msg.clarification = {
          message: event.message || "",
          options: options2
        };
        msg._renderKey = nextRenderKey();
        emit();
        break;
      }
      case "usage":
        flushContent();
        msg.usage = event.usage;
        emit();
        break;
      case "finish":
        if (event.reason && event.reason !== "stop") {
          msg.truncatedByLength = event.reason === "length";
          if (!msg.truncatedByLength) {
            msg.finishReason = event.reason;
          }
          msg._renderKey = nextRenderKey();
          emit();
        }
        break;
      case "error":
        flushContent();
        appendContent("\n\n> **\u9519\u8BEF:** " + (event.content || "\u672A\u77E5\u9519\u8BEF"));
        flushContent();
        break;
      case "done":
        break;
    }
  }
  async function runStream(target, payload) {
    contentBuffer = "";
    thinkingBuffer = "";
    streamTarget = target;
    loading = true;
    emit();
    onAfterSend && onAfterSend();
    controller = createAbortController();
    try {
      const streamPayload = payload.chatId === void 0 && activeChatId != null ? { ...payload, chatId: activeChatId } : payload;
      for await (const event of options.transport.streamChat(streamPayload, controller.signal)) {
        if (event.type === "done") {
          flushContent();
          flushThinking();
          break;
        }
        applyEvent(target, event);
      }
    } catch (err) {
      if (err && (err.name === "AbortError" || err.code === 20)) {
        target.cancelled = true;
      } else {
        flushContent();
        flushThinking();
        if (target.content) {
          target.cancelled = true;
          notifier.error && notifier.error("\u751F\u6210\u4E2D\u65AD\uFF0C\u5DF2\u4FDD\u7559\u5DF2\u751F\u6210\u5185\u5BB9");
        } else {
          notifier.error && notifier.error("\u8BF7\u6C42\u5931\u8D25");
          target.content = err && err.message || "\u8BF7\u6C42\u5931\u8D25\uFF0C\u8BF7\u91CD\u8BD5\u3002";
        }
        emit();
      }
    } finally {
      flushContent();
      flushThinking();
      if (target.stages) {
        for (const s of target.stages) if (s.status === "running") s.status = "done";
      }
      target.streaming = false;
      loading = false;
      controller = null;
      streamTarget = null;
      emit();
    }
  }
  async function send(payload) {
    if (loading) return;
    const question = (payload.question || "").trim();
    const hasImage = !!payload.imageDataUrl;
    if (!question && !hasImage) return;
    const userContent = question || (hasImage ? "[Image]" : "");
    if (hasImage && payload.imageDataUrl) {
      saveChatImage(activeChatId || "pending", payload.imageDataUrl);
    }
    const userMsg = {
      _id: nextMsgId(),
      role: "user",
      content: userContent,
      createTime: currentTime(),
      image: payload.imageDataUrl || void 0
    };
    const assistantMsg = {
      _id: nextMsgId(),
      role: "assistant",
      content: "",
      createTime: currentTime(),
      usage: null,
      cancelled: false,
      progress: null,
      toolCalls: null,
      webSearchResults: null,
      optimizedSearchQuery: null,
      knowledgeTraces: null,
      reasoning: "",
      _reasoningExpanded: true,
      toolChain: [],
      _loopExpanded: true,
      stages: [
        {
          name: "__pending_submit",
          status: "running",
          detail: "\u6B63\u5728\u63D0\u4EA4\u7ED9 AI \u670D\u52A1\u2026",
          durationMs: null,
          startAt: Date.now()
        }
      ],
      _renderKey: 0,
      streaming: true
    };
    messages = [...messages, userMsg, assistantMsg];
    await runStream(assistantMsg, payload);
    const answer = assistantMsg.content;
    if (answer && !assistantMsg.cancelled && onSaveSession) {
      try {
        onSaveSession(userContent, answer);
      } catch {
      }
    }
  }
  async function resume(msg, base) {
    if (loading) return;
    const prefix = msg.content || "";
    if (!prefix.trim()) return;
    const originQuestion = findQuestionBefore(msg);
    msg.cancelled = false;
    msg.interrupted = false;
    msg.truncatedByLength = false;
    msg.streaming = true;
    msg.progress = null;
    msg.stages = [];
    msg.toolChain = [];
    msg.toolCalls = null;
    msg._turnCardExpanded = void 0;
    msg._renderKey = nextRenderKey();
    await runStream(msg, {
      ...base || {},
      question: "\u7EE7\u7EED",
      resumePrefix: prefix,
      resumeQuestion: originQuestion || void 0
    });
    stitchResumeSeam(msg, prefix);
  }
  function stitchResumeSeam(msg, prefix) {
    const full = msg.content || "";
    if (!prefix || !full.startsWith(prefix) || full.length <= prefix.length) return;
    const continuation = full.slice(prefix.length);
    const maxOverlap = Math.min(RESUME_SEAM_MAX_OVERLAP, prefix.length, continuation.length);
    for (let size = maxOverlap; size >= RESUME_SEAM_MIN_OVERLAP; size--) {
      if (prefix.slice(prefix.length - size) === continuation.slice(0, size)) {
        msg.content = prefix + continuation.slice(size);
        msg._renderKey = nextRenderKey();
        emit();
        return;
      }
    }
  }
  function findQuestionBefore(msg) {
    const idx = messages.findIndex((m) => m._id === msg._id);
    for (let i = (idx >= 0 ? idx : messages.length) - 1; i >= 0; i--) {
      if (messages[i].role === "user") return messages[i].content || "";
    }
    return "";
  }
  function saveChatImage(chatId, dataUrl) {
    if (!storage || !chatId || !dataUrl) return;
    try {
      const key = CHAT_IMG_STORAGE_PREFIX + chatId;
      const list = JSON.parse(storage.get(key) || "[]");
      list.push(dataUrl);
      if (list.length > CHAT_IMG_CACHE_MAX) {
        list.splice(0, list.length - CHAT_IMG_CACHE_MAX);
      }
      storage.set(key, JSON.stringify(list));
    } catch {
    }
  }
  function loadChatImages(chatId) {
    if (!storage || !chatId) return [];
    try {
      const list = JSON.parse(storage.get(CHAT_IMG_STORAGE_PREFIX + chatId) || "[]");
      return Array.isArray(list) ? list : [];
    } catch {
      return [];
    }
  }
  function clearChatImages(chatId) {
    if (!storage || !chatId) return;
    try {
      storage.remove(CHAT_IMG_STORAGE_PREFIX + chatId);
    } catch {
    }
  }
  function sendClarification(msg, option, base) {
    const draft = String(msg.clarificationDraft || "").trim();
    const refined = option && option.value || draft;
    if (!refined) return;
    msg.clarificationDraft = "";
    const payload = { ...base || {}, question: refined };
    return send(payload);
  }
  return {
    getMessages: () => messages.slice(),
    getActiveChatId: () => activeChatId,
    setActiveChatId: (id) => {
      activeChatId = id;
    },
    isLoading: () => loading,
    send,
    resume,
    cancel() {
      if (controller) controller.abort();
      loading = false;
    },
    subscribe(listener) {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
    flushNow() {
      flushContent();
      flushThinking();
    },
    reset() {
      messages = [];
      contentBuffer = "";
      thinkingBuffer = "";
      activeChatId = null;
      emit();
    },
    dispose() {
      if (contentTimer) clearTimeout(contentTimer);
      if (thinkingTimer) clearTimeout(thinkingTimer);
      contentTimer = null;
      thinkingTimer = null;
      if (controller) controller.abort();
      listeners.clear();
    },
    setOnSaveSession(cb) {
      onSaveSession = cb;
    },
    setOnAfterSend(cb) {
      onAfterSend = cb;
    },
    saveChatImage,
    loadChatImages,
    clearChatImages,
    sendClarification
  };
}

// core/src/ai/agent.ts
function normalizeAgents(rows) {
  if (!Array.isArray(rows)) return [];
  return rows.map((a) => ({
    ...a,
    id: a.id,
    agentCode: a.agentCode || a.id,
    agentName: a.agentName || "",
    agentDesc: a.agentDesc || "",
    agentAvatar: a.agentAvatar || "",
    knowledgeIds: a.knowledgeIds || "[]",
    ragType: a.ragType || ""
  }));
}
function parseKnowledgeIds(raw) {
  if (raw == null || raw === "") return [];
  let list = raw;
  if (typeof raw === "string") {
    try {
      list = JSON.parse(raw);
    } catch {
      list = raw.split(",");
    }
  }
  if (!Array.isArray(list)) return [];
  return list.map((v) => Number(v)).filter((n) => Number.isFinite(n));
}
function filterKnowledgesByAgent(all, agent) {
  if (!agent) return all;
  const ids = parseKnowledgeIds(agent.knowledgeIds);
  if (ids.length === 0) return all;
  return all.filter((k) => ids.includes(Number(k.id)));
}
function filterRagStrategies(all, agent) {
  if (!agent || !agent.ragType) return all;
  return all.filter((r) => r.code === agent.ragType || r.name === agent.ragType);
}
function resolveKnowledgeIds(contextKbs, agentKnowledges) {
  const picked = (contextKbs || []).map((v) => Number(v)).filter((n) => Number.isFinite(n));
  if (picked.length > 0) return picked;
  return (agentKnowledges || []).map((k) => Number(k.id)).filter((n) => Number.isFinite(n));
}

// core/src/ai/history.ts
function normalizeHistoryMessages(list) {
  if (!Array.isArray(list)) return [];
  return list.map((m, idx) => {
    const toolCalls = Array.isArray(m.toolCalls) ? m.toolCalls.map((tc) => ({
      toolName: tc.toolName || tc.tool_name || "",
      arguments: tc.arguments,
      result: tc.result,
      success: tc.success,
      durationMs: tc.durationMs ?? tc.duration_ms ?? null,
      reasoning: tc.reasoning || "",
      round: tc.round
    })) : null;
    return {
      ...m,
      _id: m._id || m.id || `hist_${idx}`,
      role: m.role || "assistant",
      content: m.content || "",
      createTime: m.createTime || currentTime(),
      toolCalls,
      toolChain: toolCalls ? toolCalls.map((t, i) => ({ ...t, _idx: i, _expanded: false })) : [],
      streaming: false
    };
  });
}
function createSessionCache(options) {
  const prefix = options.keyPrefix || "huyu_chat_cache_";
  const maxMessages = options.maxMessages ?? 50;
  return {
    save(chatId, messages) {
      if (!chatId || !messages || messages.length === 0) return;
      try {
        const slice = messages.slice(-maxMessages);
        options.storage.set(prefix + chatId, JSON.stringify(slice));
      } catch {
      }
    },
    load(chatId) {
      if (!chatId) return [];
      try {
        const raw = options.storage.get(prefix + chatId);
        if (!raw) return [];
        const list = JSON.parse(raw);
        return Array.isArray(list) ? list : [];
      } catch {
        return [];
      }
    },
    remove(chatId) {
      if (!chatId) return;
      try {
        options.storage.remove(prefix + chatId);
      } catch {
      }
    },
    clearAll(chatIds = []) {
      for (const id of chatIds) this.remove(id);
    }
  };
}
function sortSessions(list) {
  return list.slice().sort((a, b) => {
    const ta = new Date(a.updateTime || a.createTime || 0).getTime();
    const tb = new Date(b.updateTime || b.createTime || 0).getTime();
    return tb - ta;
  });
}
function deriveSessionTitle(question) {
  const q = (question || "").trim().replace(/\s+/g, " ");
  if (!q) return "\u65B0\u5BF9\u8BDD";
  return q.length > 20 ? q.slice(0, 20) + "\u2026" : q;
}

// core/src/ai/faq.ts
function normalizeQuickQuestions(rows) {
  if (!Array.isArray(rows)) return [];
  const list = rows.filter((r) => r && (r.question || r.title)).map((r) => ({
    id: r.id,
    text: String(r.question || r.title || ""),
    agentSpecific: !!(r.fields2 && String(r.fields2).trim() !== ""),
    raw: r
  }));
  return list.sort((a, b) => Number(b.agentSpecific) - Number(a.agentSpecific));
}
function flattenFaqTree(nodes) {
  if (!Array.isArray(nodes)) return [];
  const out = [];
  const walk = (list) => {
    for (const n of list) {
      if (!n) continue;
      out.push(n);
      if (Array.isArray(n.children) && n.children.length) walk(n.children);
    }
  };
  walk(nodes);
  return out;
}
function searchFaq(items, keyword) {
  const kw = (keyword || "").trim().toLowerCase();
  if (!kw) return items;
  return items.filter((it) => {
    const q = String(it.question || "").toLowerCase();
    const a = String(it.answer || "").toLowerCase();
    return q.includes(kw) || a.includes(kw);
  });
}

// core/src/ai/markdown.ts
var DEFAULT_OPTIONS = {
  fontSize: 14,
  color: "#2b2f36",
  mutedColor: "#8a919f",
  accentColor: "#328cfa",
  codeBg: "#f4f6fa",
  borderColor: "#e6ebf2"
};
function buildStyles(o) {
  const lh = 1.75;
  return {
    p: `margin:0 0 8px;font-size:${o.fontSize}px;line-height:${lh};color:${o.color};word-break:break-word;`,
    h: [
      `margin:12px 0 6px;font-size:${o.fontSize + 6}px;font-weight:700;line-height:1.4;color:${o.color};`,
      `margin:12px 0 6px;font-size:${o.fontSize + 4}px;font-weight:700;line-height:1.4;color:${o.color};`,
      `margin:10px 0 6px;font-size:${o.fontSize + 2}px;font-weight:700;line-height:1.4;color:${o.color};`,
      `margin:10px 0 6px;font-size:${o.fontSize + 1}px;font-weight:700;line-height:1.4;color:${o.color};`,
      `margin:8px 0 4px;font-size:${o.fontSize}px;font-weight:700;line-height:1.4;color:${o.color};`,
      `margin:8px 0 4px;font-size:${o.fontSize}px;font-weight:700;line-height:1.4;color:${o.mutedColor};`
    ],
    ul: `margin:0 0 8px;padding-left:18px;`,
    ol: `margin:0 0 8px;padding-left:18px;`,
    li: `margin:0 0 4px;font-size:${o.fontSize}px;line-height:${lh};color:${o.color};word-break:break-word;`,
    quote: `margin:0 0 8px;padding:6px 10px;border-left:3px solid ${o.borderColor};background:${o.codeBg};font-size:${o.fontSize}px;line-height:${lh};color:${o.mutedColor};`,
    code: `padding:1px 5px;border-radius:4px;background:${o.codeBg};color:${o.accentColor};font-size:${o.fontSize - 1}px;`,
    pre: `margin:0 0 8px;padding:10px 12px;border-radius:8px;background:${o.codeBg};overflow-x:auto;`,
    hr: `margin:12px 0;border:0;border-top:1px solid ${o.borderColor};`,
    table: `margin:0 0 8px;width:100%;border-collapse:collapse;font-size:${o.fontSize - 1}px;`,
    th: `padding:6px 8px;border:1px solid ${o.borderColor};background:${o.codeBg};font-weight:600;color:${o.color};text-align:left;`,
    td: `padding:6px 8px;border:1px solid ${o.borderColor};color:${o.color};`,
    a: `color:${o.accentColor};text-decoration:none;word-break:break-all;`,
    del: `color:${o.mutedColor};`,
    img: `max-width:100%;height:auto;border-radius:6px;`,
    strong: `font-weight:700;color:${o.color};`
  };
}
function escapeRichText(str) {
  return String(str == null ? "" : str).replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;").replace(/'/g, "&#39;");
}
function renderInline(text, s, o) {
  let body = escapeRichText(text);
  const held = [];
  const hold = (html) => {
    held.push(html);
    return `${held.length - 1}`;
  };
  body = body.replace(/`([^`\n]+)`/g, (_m, code) => hold(`<code style="${s.code}">${code}</code>`));
  body = body.replace(
    /!\[([^\]]*)\]\(([^)\s]+)[^)]*\)/g,
    (_m, alt, url) => hold(`<img src="${url}" alt="${alt}" style="${s.img}" />`)
  );
  body = body.replace(/\[([^\]]+)\]\(([^)\s]+)[^)]*\)/g, (_m, label) => hold(`<a style="${s.a}">${label}</a>`));
  body = body.replace(
    /(^|[\s(])(https?:\/\/[^\s<>()]+)/g,
    (_m, pre, url) => `${pre}${hold(`<a style="${s.a}">${url}</a>`)}`
  );
  body = body.replace(/\*\*([^*\n]+)\*\*/g, `<strong style="${s.strong}">$1</strong>`);
  body = body.replace(/__([^_\n]+)__/g, `<strong style="${s.strong}">$1</strong>`);
  body = body.replace(/~~([^~\n]+)~~/g, `<del style="${s.del}">$1</del>`);
  body = body.replace(/(^|[^*])\*([^*\n]+)\*/g, "$1<em>$2</em>");
  body = body.replace(/(^|[^_\w])_([^_\n]+)_/g, "$1<em>$2</em>");
  body = body.replace(/\u0001(\d+)\u0001/g, (_m, i) => held[Number(i)] || "");
  body = body.replace(/\n/g, "<br/>");
  return body;
}
function splitRow(line) {
  const trimmed = line.trim().replace(/^\|/, "").replace(/\|$/, "");
  const cells = [];
  let cur = "";
  for (let i = 0; i < trimmed.length; i++) {
    const ch = trimmed[i];
    if (ch === "\\" && trimmed[i + 1] === "|") {
      cur += "|";
      i++;
    } else if (ch === "|") {
      cells.push(cur.trim());
      cur = "";
    } else {
      cur += ch;
    }
  }
  cells.push(cur.trim());
  return cells;
}
var RE_FENCE = /^\s*(```|~~~)\s*([\w+#-]*)/;
var RE_HEADING = /^\s*(#{1,6})\s+(.*)$/;
var RE_HR = /^\s*(?:-{3,}|\*{3,}|_{3,})\s*$/;
var RE_QUOTE = /^\s*>\s?/;
var RE_UL = /^\s*[-*+]\s+(.*)$/;
var RE_OL = /^\s*\d+[.)]\s+(.*)$/;
var RE_TABLE_SEP = /^\s*\|?\s*:?-{2,}:?\s*(\|\s*:?-{2,}:?\s*)*\|?\s*$/;
function markdownToRichText(md, options = {}) {
  const o = { ...DEFAULT_OPTIONS, ...options };
  const s = buildStyles(o);
  const text = String(md == null ? "" : md).replace(/\r\n?/g, "\n");
  if (!text.trim()) return "";
  const lines = text.split("\n");
  const out = [];
  let i = 0;
  while (i < lines.length) {
    const line = lines[i];
    const fence = RE_FENCE.exec(line);
    if (fence) {
      const marker = fence[1];
      const lang = fence[2] || "";
      const buf2 = [];
      i++;
      while (i < lines.length) {
        const cur = lines[i];
        if (cur.trim().startsWith(marker) && cur.trim().replace(/[`~]/g, "") === "") {
          i++;
          break;
        }
        buf2.push(cur);
        i++;
      }
      const code = escapeRichText(buf2.join("\n"));
      const langHint = lang ? `<div style="margin:0 0 4px;font-size:${o.fontSize - 3}px;color:${o.mutedColor};">${escapeRichText(lang)}</div>` : "";
      out.push(
        `<pre style="${s.pre}">${langHint}<code style="font-size:${o.fontSize - 1}px;line-height:1.6;color:${o.color};font-family:Menlo,Consolas,monospace;white-space:pre-wrap;word-break:break-all;">${code}</code></pre>`
      );
      continue;
    }
    const heading = RE_HEADING.exec(line);
    if (heading) {
      const level = Math.min(heading[1].length, 6);
      out.push(`<h${level} style="${s.h[level - 1]}">${renderInline(heading[2], s, o)}</h${level}>`);
      i++;
      continue;
    }
    if (RE_HR.test(line)) {
      out.push(`<hr style="${s.hr}" />`);
      i++;
      continue;
    }
    if (RE_QUOTE.test(line)) {
      const buf2 = [];
      while (i < lines.length && RE_QUOTE.test(lines[i])) {
        buf2.push(lines[i].replace(RE_QUOTE, ""));
        i++;
      }
      out.push(`<blockquote style="${s.quote}">${renderInline(buf2.join("\n"), s, o)}</blockquote>`);
      continue;
    }
    if (RE_UL.test(line)) {
      const items = [];
      while (i < lines.length) {
        const m = RE_UL.exec(lines[i]);
        if (m) {
          items.push(m[1]);
          i++;
        } else if (lines[i].trim() === "") {
          const next = lines[i + 1] || "";
          if (RE_UL.test(next) || /^\s{2,}/.test(next)) {
            i++;
          } else {
            break;
          }
        } else if (/^\s{2,}\S/.test(lines[i])) {
          items[items.length - 1] = (items[items.length - 1] || "") + "\n" + lines[i].trim();
          i++;
        } else {
          break;
        }
      }
      const lis = items.map((it) => `<li style="${s.li}">${renderInline(it, s, o)}</li>`).join("");
      out.push(`<ul style="${s.ul}">${lis}</ul>`);
      continue;
    }
    if (RE_OL.test(line)) {
      const items = [];
      while (i < lines.length) {
        const m = RE_OL.exec(lines[i]);
        if (m) {
          items.push(m[1]);
          i++;
        } else if (lines[i].trim() === "") {
          const next = lines[i + 1] || "";
          if (RE_OL.test(next) || /^\s{2,}/.test(next)) {
            i++;
          } else {
            break;
          }
        } else if (/^\s{2,}\S/.test(lines[i])) {
          items[items.length - 1] = (items[items.length - 1] || "") + "\n" + lines[i].trim();
          i++;
        } else {
          break;
        }
      }
      const lis = items.map((it) => `<li style="${s.li}">${renderInline(it, s, o)}</li>`).join("");
      out.push(`<ol style="${s.ol}">${lis}</ol>`);
      continue;
    }
    if (line.indexOf("|") >= 0 && i + 1 < lines.length && RE_TABLE_SEP.test(lines[i + 1])) {
      const header = splitRow(line);
      i += 2;
      const rows = [];
      while (i < lines.length && lines[i].indexOf("|") >= 0 && lines[i].trim() !== "") {
        rows.push(splitRow(lines[i]));
        i++;
      }
      const th = header.map((c) => `<th style="${s.th}">${renderInline(c, s, o)}</th>`).join("");
      const trs = rows.map((r) => {
        const cells = [];
        for (let c = 0; c < header.length; c++) {
          cells.push(`<td style="${s.td}">${renderInline(r[c] || "", s, o)}</td>`);
        }
        return `<tr>${cells.join("")}</tr>`;
      }).join("");
      out.push(
        `<table style="${s.table}"><thead><tr>${th}</tr></thead>${trs ? `<tbody>${trs}</tbody>` : ""}</table>`
      );
      continue;
    }
    if (line.trim() === "") {
      i++;
      continue;
    }
    const buf = [line];
    i++;
    while (i < lines.length) {
      const cur = lines[i];
      if (cur.trim() === "" || RE_FENCE.test(cur) || RE_HEADING.test(cur) || RE_HR.test(cur) || RE_QUOTE.test(cur) || RE_UL.test(cur) || RE_OL.test(cur) || cur.indexOf("|") >= 0 && RE_TABLE_SEP.test(lines[i + 1] || "")) {
        break;
      }
      buf.push(cur);
      i++;
    }
    out.push(`<p style="${s.p}">${renderInline(buf.join("\n"), s, o)}</p>`);
  }
  return out.join("");
}

// core/src/ai/contextMeter.ts
var DEFAULT_LEVELS = { warning: 0.8, critical: 0.95 };
function resolveLevel(ratio, levels = DEFAULT_LEVELS) {
  if (!Number.isFinite(ratio) || ratio <= 0) return "safe";
  if (ratio >= levels.critical) return "critical";
  if (ratio >= levels.warning) return "warning";
  if (ratio >= 0.5) return "normal";
  return "safe";
}
function formatTokens(value) {
  const n = Number.isFinite(value) ? Math.max(Math.round(value), 0) : 0;
  if (n < 1e3) return String(n);
  if (n < 1e6) return `${(n / 1e3).toFixed(1)}K`;
  return `${(n / 1e6).toFixed(1)}M`;
}
function describeLevel(level) {
  switch (level) {
    case "critical":
      return "\u4E0A\u4E0B\u6587\u5DF2\u63A5\u8FD1\u4E0A\u9650\uFF0C\u5EFA\u8BAE\u7ACB\u5373\u538B\u7F29";
    case "warning":
      return "\u4E0A\u4E0B\u6587\u5FEB\u6EE1\u4E86\uFF0C\u53EF\u624B\u52A8\u538B\u7F29\u65E9\u671F\u5BF9\u8BDD";
    case "normal":
      return "\u4E0A\u4E0B\u6587\u4F7F\u7528\u4E2D";
    default:
      return "\u4E0A\u4E0B\u6587\u5145\u88D5";
  }
}
function ringDash(ratio, circumference) {
  const clamped = Math.min(Math.max(Number.isFinite(ratio) ? ratio : 0, 0), 1);
  const filled = clamped * circumference;
  return `${filled.toFixed(2)} ${circumference.toFixed(2)}`;
}
function fetchTransport(options) {
  const base = options.baseUrl ?? "";
  const headers = () => ({
    "Content-Type": "application/json",
    ...options.headers ? options.headers() : {}
  });
  return {
    async get(path) {
      const res = await fetch(base + path, { method: "GET", headers: headers() });
      return res.json();
    },
    async post(path) {
      const res = await fetch(base + path, { method: "POST", headers: headers(), body: "{}" });
      return res.json();
    }
  };
}
function unwrap(raw) {
  const envelope = raw ?? {};
  if (typeof envelope.code === "number" && envelope.code !== 200) {
    return { data: null, msg: envelope.msg || "\u8BF7\u6C42\u5931\u8D25" };
  }
  return { data: envelope.data ?? raw ?? null, msg: envelope.msg || "" };
}
function createContextMeter(options) {
  const transport = options.transport ?? fetchTransport(options);
  const levels = { ...DEFAULT_LEVELS, ...options.thresholds ?? {} };
  const listeners = /* @__PURE__ */ new Set();
  let state = {
    usage: null,
    level: "safe",
    percent: 0,
    loading: false,
    compacting: false,
    error: null,
    notice: null
  };
  function emit(next) {
    state = next;
    listeners.forEach((listener) => listener(state));
    if (options.onChange) options.onChange(state);
  }
  function patch(partial) {
    emit({ ...state, ...partial });
  }
  function applyUsage(usage) {
    const ratio = Number.isFinite(usage.ratio) ? usage.ratio : usage.usable > 0 ? usage.used / usage.usable : 0;
    patch({
      usage,
      level: resolveLevel(ratio, levels),
      percent: Math.round(Math.min(Math.max(ratio, 0), 1) * 100),
      loading: false,
      error: null
    });
  }
  async function refresh() {
    const chatId = options.getChatId();
    if (chatId === null || chatId === void 0 || chatId === "") {
      patch({ usage: null, level: "safe", percent: 0, loading: false, error: null });
      return null;
    }
    patch({ loading: true });
    try {
      const raw = await transport.get(`/v1/chat/${chatId}/context-usage`);
      const { data, msg } = unwrap(raw);
      if (!data) {
        patch({ loading: false, error: msg || "\u8BFB\u53D6\u4E0A\u4E0B\u6587\u5360\u7528\u5931\u8D25" });
        return null;
      }
      applyUsage(data);
      return data;
    } catch (error) {
      patch({ loading: false, error: error.message || "\u7F51\u7EDC\u5F02\u5E38" });
      return null;
    }
  }
  async function compact() {
    const chatId = options.getChatId();
    if (chatId === null || chatId === void 0 || chatId === "") return null;
    patch({ compacting: true, notice: null, error: null });
    try {
      const raw = await transport.post(`/v1/chat/${chatId}/compact`);
      const { data, msg } = unwrap(raw);
      if (!data || !data.compressedRounds) {
        patch({ compacting: false, notice: data?.message || msg || "\u6CA1\u6709\u53EF\u538B\u7F29\u7684\u5185\u5BB9\u4E86" });
        return null;
      }
      patch({ compacting: false, notice: data.message });
      await refresh();
      return data;
    } catch (error) {
      patch({ compacting: false, error: error.message || "\u538B\u7F29\u5931\u8D25" });
      return null;
    }
  }
  return {
    getState: () => ({ ...state }),
    subscribe(listener) {
      listeners.add(listener);
      listener(state);
      return () => listeners.delete(listener);
    },
    refresh,
    compact
  };
}

// core/src/ai/utf8.ts
function utf8ToString(u8) {
  let s = "";
  let i = 0;
  const len = u8.length;
  while (i < len) {
    const b = u8[i++];
    if (b < 128) {
      s += String.fromCharCode(b);
    } else if (b < 192) {
    } else if (b < 224) {
      s += String.fromCharCode((b & 31) << 6 | u8[i++] & 63);
    } else if (b < 240) {
      s += String.fromCharCode((b & 15) << 12 | (u8[i++] & 63) << 6 | u8[i++] & 63);
    } else {
      let cp = (b & 7) << 18 | (u8[i++] & 63) << 12 | (u8[i++] & 63) << 6 | u8[i++] & 63;
      cp -= 65536;
      s += String.fromCharCode(55296 + (cp >> 10), 56320 + (cp & 1023));
    }
  }
  return s;
}
function createUtf8ChunkDecoder() {
  let pending = new Uint8Array(0);
  return {
    decode(u8) {
      let buf;
      if (pending.length) {
        buf = new Uint8Array(pending.length + u8.length);
        buf.set(pending, 0);
        buf.set(u8, pending.length);
      } else {
        buf = u8;
      }
      let end = buf.length;
      let i = buf.length - 1;
      let back = 0;
      while (i >= 0 && (buf[i] & 192) === 128 && back < 3) {
        i--;
        back++;
      }
      if (i >= 0) {
        const b = buf[i];
        let need = 1;
        if ((b & 128) === 0) need = 1;
        else if ((b & 224) === 192) need = 2;
        else if ((b & 240) === 224) need = 3;
        else if ((b & 248) === 240) need = 4;
        if (buf.length - i < need) end = i;
      }
      pending = buf.slice(end);
      return end > 0 ? utf8ToString(buf.subarray(0, end)) : "";
    },
    reset() {
      pending = new Uint8Array(0);
    }
  };
}

// core/src/ai/sse-parser.ts
function createSseParser(onEvent) {
  let buffer = "";
  let finished = false;
  function parseLine(raw) {
    const line = raw.trim();
    if (!line) return;
    if (line === "data: [DONE]") {
      finished = true;
      onEvent({ type: "done" });
      return;
    }
    if (!line.startsWith("data:")) return;
    const payload = line.slice(5).trim();
    let json;
    try {
      json = JSON.parse(payload);
    } catch {
      return;
    }
    const choice = json.choices?.[0];
    if (choice?.delta?.content) onEvent({ type: "content", content: choice.delta.content });
    if (json.tool_calls) onEvent({ type: "tool_call", toolCalls: json.tool_calls });
    if (json.sources) onEvent({ type: "sources", sources: json.sources });
    if (json.usage) onEvent({ type: "usage", usage: json.usage });
    if (json.chat_id) onEvent({ type: "chatId", chatId: json.chat_id });
    if (json.error) onEvent({ type: "error", content: json.error.message ?? "\u672A\u77E5\u9519\u8BEF" });
    if (choice?.finish_reason === "stop" && !finished) {
      finished = true;
      onEvent({ type: "done" });
    }
  }
  return {
    /** 喂一段 chunk 文本（可跨块切断任意位置） */
    push(chunk) {
      buffer += chunk;
      const lines = buffer.split("\n");
      buffer = lines.pop() ?? "";
      for (const line of lines) parseLine(line);
    },
    /** 流结束后处理残留半行（[DONE] 缺失兜底） */
    flush() {
      if (buffer) {
        parseLine(buffer);
        buffer = "";
      }
      if (!finished) {
        finished = true;
        onEvent({ type: "done" });
      }
    }
  };
}
export {
  FLUSH_BUFFER_CHARS,
  FLUSH_INTERVAL,
  TURN_ACTIONS,
  buildChatRequestBody,
  buildTurnActions,
  bumpRenderKey,
  confidenceLabel,
  confidenceTagType,
  createAbortController,
  createChatProtocolParser,
  createChatSession,
  createContextMeter,
  createFetchTransport,
  createMemoryStorage,
  createSessionCache,
  createSseParser,
  createUtf8ChunkDecoder,
  currentTime,
  deriveSessionTitle,
  describeLevel,
  escapeHtml,
  escapeRichText,
  filterKnowledgesByAgent,
  filterRagStrategies,
  flattenFaqTree,
  formatTokens,
  formatToolArguments,
  formatToolResult,
  getFeedRecordImages,
  getGroupTitle,
  getHitImages,
  getReportCards,
  getStageDisplayName,
  getToolDisplayName,
  getWebSearchQuery,
  groupToolChainByRound,
  hasNonWebSearchTools,
  hasRunningStage,
  isReportTool,
  isStagePanelExpanded,
  isToolChainGroupExpanded,
  isTurnCardExpanded,
  isWebSearchExpanded,
  isWebSearchTool,
  markdownToRichText,
  matchSourceLabel,
  nextRenderKey,
  noopNotifier,
  normalizeAgents,
  normalizeHistoryMessages,
  normalizeImageUrl,
  normalizeQuickQuestions,
  openReportUrl,
  overallConfidenceLevel,
  overallConfidenceValue,
  parseKnowledgeIds,
  parseReasoningRounds,
  parseWebSearchResults,
  resolveKnowledgeIds,
  resolveLevel,
  ringDash,
  searchFaq,
  sortSessions,
  stagePanelSummary,
  summarizeToolOutcome,
  toolFlowSummary,
  truncate,
  turnCardSummary,
  utf8ToString
};
